<template>
    <div class="list-group no-border border-radius-2 mb-3">
        <div class="list-group-item list-group-item-primary d-flex justify-content-between align-items-center flex-wrap">
            <strong>{{ $t('universal-search') }}</strong>
            <div class="d-flex align-items-center flex-wrap" style="gap: 0.75rem;">
                <div class="form-check form-check-inline mb-0" v-if="$config.hasModule('semantic_search')">
                    <input
                        type="checkbox"
                        class="form-check-input"
                        id="semanticSearchToggle"
                        v-model="semantic"
                        @change="setSemantic(semantic)"
                    />
                    <label class="form-check-label" for="semanticSearchToggle">{{ $t('semantic-search') }}</label>
                </div>
                <div class="btn-group" role="group">
                    <button
                        type="button"
                        class="btn btn-sm"
                        :class="mode === 'outcomes' ? 'btn-primary' : 'btn-outline-primary'"
                        @click="setMode('outcomes')"
                    >
                        {{ $t('learning-outcomes') }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-sm"
                        :class="mode === 'curricula' ? 'btn-primary' : 'btn-outline-primary'"
                        @click="setMode('curricula')"
                    >
                        {{ $t('curricula') }}
                    </button>
                </div>
            </div>
        </div>
        <div class="list-group-item">
            <div v-if="semantic" class="pseudo-input mb-1">
                <input
                    type="search"
                    class="input-bare w-100"
                    v-model="currentInput"
                    :placeholder="semanticPlaceholder"
                    @input="onSemanticInput"
                />
            </div>
            <div v-else class="pseudo-input mb-1 d-flex flex-wrap align-items-center">
                <span
                    class="badge badge-secondary badge-sm mr-2 mb-1"
                    v-for="(keyword, index) in keywords"
                    :key="index"
                >
                    {{ keyword }}
                    <a @click="removeKeyword(index)"><b-icon icon="x"></b-icon></a>
                </span>
                <input
                    type="search"
                    class="input-bare flex-1"
                    style="min-width: 8rem;"
                    v-model="currentInput"
                    :placeholder="keywords.length ? '' : placeholder"
                    @keydown="onKeydown"
                    @blur="commitCurrentInput"
                />
            </div>
            <small class="text-muted">{{ semantic ? $t('semantic-search-hint') : $t('type-to-search-learning-outcomes') }}</small>
        </div>

        <template v-if="searched">
            <div v-if="loading" class="list-group-item text-center text-muted">
                {{ $t('loading') }}
            </div>
            <template v-else-if="results.length">
                <template v-if="mode === 'outcomes'">
                    <div
                        v-for="item in results"
                        :key="item.id"
                        class="list-group-item list-group-item-action"
                        role="button"
                        @click="selectOutcome(item)"
                    >
                        <div class="d-flex justify-content-between align-items-start">
                            <p class="mb-0 font-bold">
                                {{ item.code ? `${item.code}: ` : '' }}{{ item.title }}
                            </p>
                            <b-badge v-if="groupLabel(item)" variant="secondary" class="ml-2 flex-shrink-0">
                                {{ groupLabel(item) }}
                            </b-badge>
                        </div>
                        <p class="mb-0 text-muted small">{{ item.note || $t('no-notes') }}</p>
                        <b-list-group v-if="nodePaths(item).length" class="mt-1">
                            <b-list-group-item v-for="(path, pIndex) in nodePaths(item)" :key="pIndex">
                                {{ path }}
                            </b-list-group-item>
                        </b-list-group>
                    </div>
                </template>
                <template v-else>
                    <div
                        v-for="node in results"
                        :key="node.id"
                        class="list-group-item list-group-item-action"
                        role="button"
                        @click="selectNode(node)"
                    >
                        <p class="mb-0 font-bold">{{ node.path || node.title }}</p>
                        <b-list-group v-if="(node.learningoutcome || []).length" class="mt-1">
                            <b-list-group-item
                                v-for="lo in node.learningoutcome"
                                :key="lo.id"
                                @click.stop="selectOutcome(lo)"
                            >
                                <b-badge variant="info">{{ lo.code }}</b-badge> {{ lo.title }}
                            </b-list-group-item>
                        </b-list-group>
                        <p v-else class="mb-0 text-muted small">{{ $t('no-learning-outcomes-linked') }}</p>
                    </div>
                </template>
            </template>
            <div v-else class="list-group-item text-center text-muted">
                {{ $t('no-results') }}
            </div>
        </template>
    </div>
</template>

<script>
import LearningOutcomes from "src/modules/learningoutcomes.js";
import CurriculumMap from "src/modules/curriculummap.js";

export default {
    data() {
        return {
            mode: 'outcomes',
            semantic: false,
            currentInput: '',
            keywords: [],
            results: [],
            groups: [],
            nodes: [],
            loading: false,
            searched: false,
            minKeywordLength: 2,
            maxResults: 10,
            semanticDebounceMs: 400,
            semanticDebounceTimer: null,
            semanticRequestId: 0
        };
    },
    computed: {
        placeholder() {
            return this.mode === 'outcomes'
                ? this.$t('search-learning-outcomes-placeholder')
                : this.$t('search-curricula-placeholder');
        },
        semanticPlaceholder() {
            return this.mode === 'outcomes'
                ? this.$t('semantic-search-outcomes-placeholder')
                : this.$t('semantic-search-curricula-placeholder');
        }
    },
    mounted() {
        this.loService = new LearningOutcomes();
        this.loService.listGroups().then(res => {
            if (res && res.results) this.groups = res.results;
        });

        this.curriculumMap = new CurriculumMap();
        this.nodesPromise = this.curriculumMap.getNodes({ path_in_string: true }).then(res => {
            this.nodes = res || [];
            return this.nodes;
        });
    },
    methods: {
        setMode(newMode) {
            if (this.mode === newMode) return;
            this.mode = newMode;
            this.keywords = [];
            this.currentInput = '';
            this.results = [];
            this.searched = false;
        },
        setSemantic(enabled) {
            this.semantic = enabled;
            this.keywords = [];
            this.currentInput = '';
            this.results = [];
            this.searched = false;
        },
        onSemanticInput(e) {
            const text = e.target.value;

            if (this.semanticDebounceTimer) clearTimeout(this.semanticDebounceTimer);
            this.semanticDebounceTimer = setTimeout(() => {
                this.runSemanticSearch(text);
            }, this.semanticDebounceMs);
        },
        runSemanticSearch(text) {
            const query = (text || '').trim();

            if (query.length < this.minKeywordLength) {
                this.results = [];
                this.searched = false;
                return;
            }

            this.loading = true;
            this.searched = true;

            const requestId = ++this.semanticRequestId;

            const request = this.mode === 'curricula'
                ? this.curriculumMap.getNodes({ semantic_query: query, path_in_string: true })
                : this.loService.list({ semantic_query: query }).then(res => (res && res.results) || []);

            request
                .then(results => {
                    if (requestId !== this.semanticRequestId) return;
                    this.results = results || [];
                })
                .finally(() => {
                    if (requestId !== this.semanticRequestId) return;
                    this.loading = false;
                });
        },
        onKeydown(e) {
            if (e.key === ',' || e.key === 'Enter') {
                e.preventDefault();
                this.commitCurrentInput();
            } else if (e.key === 'Backspace' && !this.currentInput && this.keywords.length) {
                this.keywords.pop();
                this.runSearch();
            }
        },
        commitCurrentInput() {
            const term = this.currentInput.trim();
            this.currentInput = '';

            if (term.length < this.minKeywordLength) return;

            const alreadyAdded = this.keywords.some(k => k.toLowerCase() === term.toLowerCase());
            if (!alreadyAdded) this.keywords.push(term);

            this.runSearch();
        },
        removeKeyword(index) {
            this.keywords.splice(index, 1);
            this.runSearch();
        },
        runSearch() {
            if (!this.keywords.length) {
                this.results = [];
                this.searched = false;
                return;
            }

            this.loading = true;
            this.searched = true;

            if (this.mode === 'curricula') {
                // Wait for the initial node load, but read from the reactive `nodes`
                // property rather than the promise's resolved value, in case it changes later.
                this.nodesPromise.then(() => {
                    const lowerKeywords = this.keywords.map(k => k.toLowerCase());
                    this.results = this.nodes
                        .filter(node => lowerKeywords.some(k => (node.title || '').toLowerCase().includes(k)))
                        .slice(0, this.maxResults);
                }).finally(() => {
                    this.loading = false;
                });
                return;
            }

            // Note: the API's `page_size` input is not honoured by this endpoint,
            // so pass `page` to trigger pagination and trim to a preview list ourselves.
            this.loService.list({ search_terms: this.keywords, page: 1 })
                .then(res => {
                    this.results = ((res && res.results) || []).slice(0, this.maxResults);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        groupLabel(item) {
            const group = this.groups.find(g => String(g.id) === String(item.learningoutcomegroup_id));
            return group ? group.title : '';
        },
        nodePaths(item) {
            if (!item.curriculummap || !item.curriculummap.length) return [];
            return item.curriculummap
                .map(mapped => this.nodes.find(node => String(node.id) === String(mapped.id)))
                .filter(Boolean)
                .map(node => node.path);
        },
        selectOutcome(item) {
            this.$router.push(`/curriculum-mapping/learning-outcomes/edit/${item.id}`);
        },
        selectNode(node) {
            this.$router.push(`/curriculum-mapping/node/${node.id}/graph`);
        }
    }
};
</script>
